/**
 * QueryResultActions — "Use results": the verbs every query result offers
 * (LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §3.3), ad hoc or saved, builder or
 * statement mode alike:
 *
 *   Save query              → saved_reports (query_text + query_ast, §2.5)
 *   Create collection       → static Collection from the current matches
 *   Create smart collection → Collection bound to the rule (§4.2)
 *   Add to item pool        → materialized into an existing pool with a
 *                             `query:` selection reason (§4.1)
 *
 * Arriving from a pool ("Find items with a query…" on the pool editor or the
 * Arrange pool picker — DEP-034 retired PoolItemFilterModal) the URL
 * carries `?addToPool=<poolId>&returnTo=<path>`: the menu then leads with
 * "Add to <pool>" and goes back to where the person came from afterwards.
 *
 * Never a new write path: every verb goes through the store method the
 * matching page already uses (savedReportsStore.createReportPersisted,
 * collectionsStore.createCollection/addItemToCollection/bindRule,
 * itemPoolsStore.addPrioritiesToList). Pools are never made live-smart here —
 * "Feed a pool" is Phase 4 and its own governance.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bookmark, ChevronDown, FolderPlus, ListPlus, Rss, Sparkles } from 'lucide-react';
import { Button } from '../../../ui/Button';
import { Modal, Field, TextInput } from '../../../ds';
import { ItemPoolPickerModal } from './ItemPoolPickerModal';
import { ReportAudienceField } from '../../reports/ReportAudience';
import { useSavedReportsStore } from '../../../../stores/prioritization/savedReportsStore';
import { useCollectionsStore } from '../../../../stores/prioritization/collectionsStore';
import { useItemPoolsStore } from '../../../../stores/prioritization/itemPoolsStore';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import { useUIStore } from '../../../../stores/prioritization/uiStore';
import { astToFilterGroups, buildCatalog, printQuery, safeParse, type PqlQuery } from '../../../../lib/pql';
import { SMART_COLLECTION_FIELDS } from '../../../../lib/collections/smartCollectionFields';
import { bindPoolFeed } from '../../../../lib/poolFeedService';
import { useQueryFedPoolsEnabled } from '../../../../features/pools/hooks/useQueryFedPoolsEnabled';
import { audienceSentence, type ReportVisibility } from '../../../../types/prioritization/savedReports';
import type { SortSpec } from '../hooks/useItemListState';
import type { ChartType } from './chartTypes';

export interface QueryViewState {
  sort: SortSpec[];
  groupBy: string | null;
  visibleColumns: string[];
  activeChart: ChartType;
}

export interface QueryResultActionsProps {
  query: PqlQuery;
  canonicalText: string;
  /** Ids of the items the query currently matches (the rows on screen). */
  matchedItemIds: string[];
  view: QueryViewState;
  /**
   * The saved query these results were opened from. A rule created here is a
   * copy, so it records that origin (migration 725) — but only while the
   * statement in the bar is still the one that query was applied with; once it
   * is edited, the rule came from nowhere but this session.
   */
  appliedReport?: { id: string; queryText: string } | null;
  /**
   * Draw "Save query…". Default true. The Report Designer turns it off: its
   * own Save is the one save act there (one name per act, build contract §2.8).
   */
  includeSave?: boolean;
}

type Verb = 'save' | 'collection' | 'smart';

// Smart Collection rules evaluate in the store off a raw Item, so their
// vocabulary is narrower than the item list's (smartCollectionFields.ts).
const SMART_CATALOG = buildCatalog(SMART_COLLECTION_FIELDS);

const CHUNK = 10;

export function QueryResultActions({ query, canonicalText, matchedItemIds, view, appliedReport, includeSave = true }: QueryResultActionsProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [verb, setVerb] = useState<Verb | null>(null);
  const [poolPickerOpen, setPoolPickerOpen] = useState(false);
  /** What the pool picker is for: materialize current matches, or bind the query as the pool's feed. */
  const [poolPickerMode, setPoolPickerMode] = useState<'add' | 'feed'>('add');
  const [busy, setBusy] = useState(false);
  const queryFedPoolsEnabled = useQueryFedPoolsEnabled();
  const menuRef = useRef<HTMLDivElement>(null);

  const createReportPersisted = useSavedReportsStore((s) => s.createReportPersisted);
  const createCollection = useCollectionsStore((s) => s.createCollection);
  const addItemToCollection = useCollectionsStore((s) => s.addItemToCollection);
  const bindRule = useCollectionsStore((s) => s.bindRule);
  const addPrioritiesToList = useItemPoolsStore((s) => s.addPrioritiesToList);
  const getItemPoolById = useItemPoolsStore((s) => s.getItemPoolById);
  const userId = useUserStore((s) => s.userId);
  const addNotification = useUIStore((s) => s.addNotification);

  // Opened from a pool: add straight to it, then go back there.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const targetPoolId = searchParams.get('addToPool');
  const targetPool = useItemPoolsStore((s) => (targetPoolId ? s.getItemPoolById(targetPoolId) : undefined));
  const isItemPoolLocked = useItemPoolsStore((s) => s.isItemPoolLocked);
  const targetPoolLocked = targetPool ? isItemPoolLocked(targetPool.id) || !!targetPool.isLocked : false;
  const returnTo = safeReturnPath(searchParams.get('returnTo'));

  const hasQuery = canonicalText.trim().length > 0;
  const matchCount = matchedItemIds.length;
  /** Null once the statement has been edited away from the saved query it was opened from. */
  const sourceReportId = appliedReport && appliedReport.queryText === canonicalText ? appliedReport.id : null;

  // A rule the collection store can't evaluate is refused up front, with the
  // parser's own reason — not bound and silently matching nothing.
  const smartBlocker = useMemo(() => {
    if (!hasQuery) return 'Write a query first — a smart collection needs a rule.';
    const parsed = safeParse(canonicalText, SMART_CATALOG);
    return parsed.ok ? null : `Smart collections can't evaluate this rule: ${parsed.error.message}`;
  }, [canonicalText, hasQuery]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const pick = (next: Verb | 'pool' | 'feed') => {
    setMenuOpen(false);
    if (next === 'pool' || next === 'feed') {
      setPoolPickerMode(next === 'feed' ? 'feed' : 'add');
      setPoolPickerOpen(true);
    } else {
      setVerb(next);
    }
  };

  // A feed rule evaluates in the pool store off a raw Item — same vocabulary
  // as a Smart Collection rule, so the same up-front check applies.
  const feedBlocker = useMemo(() => {
    if (!hasQuery) return 'Write a query first — a feed needs a rule.';
    const parsed = safeParse(canonicalText, SMART_CATALOG);
    return parsed.ok ? null : `A pool feed can't evaluate this rule: ${parsed.error.message}`;
  }, [canonicalText, hasQuery]);

  const feedPool = async (poolId: string) => {
    setPoolPickerOpen(false);
    const pool = getItemPoolById(poolId);
    if (!pool) return;
    const parsed = safeParse(canonicalText, SMART_CATALOG);
    if (!parsed.ok) {
      addNotification({ type: 'error', title: 'Feed not attached', message: parsed.error.message, duration: 5000 });
      return;
    }
    if (pool.feedQueryId) {
      addNotification({ type: 'error', title: `${pool.name} already has a feed`, message: 'Detach its current feed on the pool page first.', duration: 5000 });
      return;
    }
    const res = await bindPoolFeed(pool, printQuery(parsed.query), parsed.query.where, sourceReportId);
    if (res.error) {
      addNotification({ type: 'error', title: 'Feed not attached', message: res.error, duration: 5000 });
      return;
    }
    addNotification({
      type: 'success',
      title: `${pool.name} is now fed by this query`,
      message: 'Nothing was added yet. Open the pool and use Refresh from feed to review the matches; the feed is severed when the pool locks.',
      duration: 6000,
    });
  };

  const saveQuery = async (name: string, description: string, visibility: ReportVisibility) => {
    const { error } = await createReportPersisted({
      name,
      description: description || undefined,
      filterGroups: astToFilterGroups(query.where) ?? [],
      queryText: canonicalText,
      queryAst: query.where,
      sort: query.orderBy.length > 0 ? query.orderBy : view.sort,
      groupBy: view.groupBy,
      visibleColumns: view.visibleColumns,
      activeChart: view.activeChart,
      visibility,
    });
    if (error) {
      addNotification({ type: 'error', title: 'Query not saved', message: error, duration: 5000 });
      return;
    }
    addNotification({ type: 'success', title: 'Query saved', message: `"${name}" is in Reports. ${audienceSentence(visibility)}`, duration: 4000 });
  };

  const createStaticCollection = async (name: string, description: string) => {
    const collection = await createCollection(name, description || undefined, userId ?? undefined);
    if (!collection) {
      addNotification({ type: 'error', title: 'Could not create collection', message: 'Check that you are signed in to a workspace.', duration: 4000 });
      return;
    }
    let added = 0;
    for (let i = 0; i < matchedItemIds.length; i += CHUNK) {
      const results = await Promise.all(
        matchedItemIds.slice(i, i + CHUNK).map((id) => addItemToCollection(collection.id, id, userId ?? undefined)),
      );
      added += results.filter(Boolean).length;
    }
    addNotification({
      type: added === matchedItemIds.length ? 'success' : 'warning',
      title: 'Collection created',
      message: `"${name}" holds ${added} of ${matchedItemIds.length} matching item${matchedItemIds.length === 1 ? '' : 's'}.`,
      duration: 4000,
    });
  };

  const createSmartCollection = async (name: string, description: string) => {
    const collection = await createCollection(name, description || undefined, userId ?? undefined);
    if (!collection) {
      addNotification({ type: 'error', title: 'Could not create collection', message: 'Check that you are signed in to a workspace.', duration: 4000 });
      return;
    }
    // Bind the rule as the collection store will evaluate it: re-parsed against
    // its own vocabulary, so leaf field keys are the ones
    // getSmartCollectionFieldValue resolves (item-list `altitude` → `altitudeLevel`).
    const parsed = safeParse(canonicalText, SMART_CATALOG);
    if (!parsed.ok) {
      addNotification({ type: 'error', title: 'Rule not bound', message: parsed.error.message, duration: 5000 });
      return;
    }
    const ok = await bindRule(collection.id, printQuery(parsed.query), parsed.query.where, userId ?? 'system', sourceReportId);
    addNotification(
      ok
        ? { type: 'success', title: 'Smart collection created', message: `"${name}" will keep matching new items. Pin or exclude specific items on its page.`, duration: 4000 }
        : { type: 'warning', title: 'Collection created without its rule', message: `"${name}" exists but the rule did not bind — open it and add the rule there.`, duration: 5000 },
    );
  };

  const runVerb = async (name: string, description: string, visibility: ReportVisibility) => {
    if (!verb) return;
    setBusy(true);
    try {
      if (verb === 'save') await saveQuery(name, description, visibility);
      else if (verb === 'collection') await createStaticCollection(name, description);
      else await createSmartCollection(name, description);
      setVerb(null);
    } finally {
      setBusy(false);
    }
  };

  const addToPool = (poolId: string, thenReturn = false) => {
    setPoolPickerOpen(false);
    const pool = getItemPoolById(poolId);
    if (!pool) return;
    // The pool lock is pool_locked_at (isItemPoolLocked); `isLocked` is the
    // separate publication-review lock and is not loaded from the row.
    if (isItemPoolLocked(pool.id) || pool.isLocked) {
      addNotification({
        type: 'error',
        title: 'Pool is locked',
        message: 'A locked pool has one membership truth. Items that match after lock wait for the next cycle.',
        duration: 5000,
      });
      return;
    }
    const reason = `query: ${canonicalText}`.slice(0, 500);
    const result = addPrioritiesToList(poolId, matchedItemIds, pool.cycleId, userId ?? undefined, reason, 'filter');
    if (result.error) {
      addNotification({ type: 'error', title: 'Could not add to pool', message: result.error, duration: 4000 });
      return;
    }
    const skipped = matchedItemIds.length - result.added.length;
    addNotification({
      type: 'success',
      title: `Added to ${pool.name}`,
      message: `${result.added.length} item${result.added.length === 1 ? '' : 's'} added${skipped > 0 ? `, ${skipped} already in the pool` : ''}.`,
      duration: 4000,
    });
    if (thenReturn && returnTo) navigate(returnTo);
  };

  const modalCopy: Record<Verb, { title: string; description: string; cta: string; placeholder: string }> = {
    save: {
      title: 'Save query',
      description: 'Saved with the current columns and sort. Apply it later from Reports.',
      cta: 'Save query',
      placeholder: 'e.g. Stale risks nobody owns',
    },
    collection: {
      title: 'Create collection',
      description: `A static collection of the ${matchCount} item${matchCount === 1 ? '' : 's'} matching right now. It will not follow the query.`,
      cta: 'Create collection',
      placeholder: 'e.g. Q4 churn candidates',
    },
    smart: {
      title: 'Create smart collection',
      description: 'Bound to this rule: membership is re-evaluated every time it is read. You can pin or exclude individual items.',
      cta: 'Create smart collection',
      placeholder: 'e.g. Open enterprise risks',
    },
  };

  return (
    <div ref={menuRef} className="relative shrink-0">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setMenuOpen((o) => !o)}
        disabled={!hasQuery && matchCount === 0}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="!px-2 !py-1 !text-[11px] !font-medium !gap-1"
        title={includeSave ? 'Save this query or turn its results into a collection or pool' : 'Turn these results into a collection or pool'}
      >
        Use results
        <ChevronDown className="h-3 w-3" />
      </Button>

      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 w-72 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] shadow-lg py-1"
        >
          {targetPool && (
            <MenuItem
              icon={<ListPlus className="h-3.5 w-3.5" />}
              label={`Add to ${targetPool.name}`}
              hint={targetPoolLocked ? 'This pool is locked — items that match now wait for the next cycle' : `${matchCount} current match${matchCount === 1 ? '' : 'es'}, then back to the pool`}
              disabled={matchCount === 0 || targetPoolLocked}
              onClick={() => {
                setMenuOpen(false);
                addToPool(targetPool.id, true);
              }}
            />
          )}
          {includeSave && (
            <MenuItem icon={<Bookmark className="h-3.5 w-3.5" />} label="Save query…" hint="Reports library" disabled={!hasQuery} onClick={() => pick('save')} />
          )}
          <MenuItem icon={<FolderPlus className="h-3.5 w-3.5" />} label="Create collection…" hint={`${matchCount} current match${matchCount === 1 ? '' : 'es'}, static`} disabled={matchCount === 0} onClick={() => pick('collection')} />
          <MenuItem icon={<Sparkles className="h-3.5 w-3.5" />} label="Create smart collection…" hint={smartBlocker ?? 'Follows the rule as items change'} disabled={smartBlocker !== null} onClick={() => pick('smart')} />
          <MenuItem icon={<ListPlus className="h-3.5 w-3.5" />} label="Add to item pool…" hint={`${matchCount} current match${matchCount === 1 ? '' : 'es'}; pool does not follow the query`} disabled={matchCount === 0} onClick={() => pick('pool')} />
          {queryFedPoolsEnabled && (
            <MenuItem icon={<Rss className="h-3.5 w-3.5" />} label="Feed a pool…" hint={feedBlocker ?? 'Bind this query as the pool’s feed; refresh is explicit, severed at lock'} disabled={feedBlocker !== null} onClick={() => pick('feed')} />
          )}
        </div>
      )}

      {verb && (
        <NameModal
          key={verb}
          open
          busy={busy}
          {...modalCopy[verb]}
          statement={canonicalText}
          asksAudience={verb === 'save'}
          onCancel={() => setVerb(null)}
          onConfirm={(name, description, visibility) => void runVerb(name, description, visibility)}
        />
      )}

      <ItemPoolPickerModal
        open={poolPickerOpen}
        onClose={() => setPoolPickerOpen(false)}
        onSelect={poolPickerMode === 'feed' ? (id) => void feedPool(id) : (id) => addToPool(id)}
      />
    </div>
  );
}

/** Only an in-app path may be a return target — never another site. */
function safeReturnPath(raw: string | null): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

function MenuItem({
  icon,
  label,
  hint,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      title={disabled ? hint : undefined}
      className="!w-full !justify-start !items-start !gap-2 !px-3 !py-1.5 !text-left !font-normal !rounded-none disabled:opacity-50"
    >
      <span className="mt-0.5 text-[var(--text-ter)]">{icon}</span>
      <span className="min-w-0">
        <span className="block text-[12px] text-[var(--text-pri)]">{label}</span>
        <span className="block text-[10px] leading-3 text-[var(--text-ter)] truncate">{hint}</span>
      </span>
    </Button>
  );
}

function NameModal({
  open,
  title,
  description,
  cta,
  placeholder,
  statement,
  asksAudience,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  cta: string;
  placeholder: string;
  statement: string;
  /** Saving a query asks who can see it. Making a collection does not. */
  asksAudience: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (name: string, description: string, visibility: ReportVisibility) => void;
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [visibility, setVisibility] = useState<ReportVisibility>('workspace');
  const canConfirm = name.trim().length > 0 && !busy;
  const submit = () => {
    if (canConfirm) onConfirm(name.trim(), desc.trim(), visibility);
  };
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="cta" size="sm" onClick={submit} disabled={!canConfirm}>{busy ? 'Working…' : cta}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name" required htmlFor="query-verb-name">
          <TextInput
            id="query-verb-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            placeholder={placeholder}
          />
        </Field>
        <Field label="Description" hint="Optional" htmlFor="query-verb-desc">
          <TextInput id="query-verb-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is this for?" />
        </Field>
        {asksAudience && <ReportAudienceField value={visibility} onChange={setVisibility} />}
        {statement && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-ter)] mb-1">Query</div>
            <code className="block font-mono text-[11px] text-[var(--text-sec)] break-words border border-[var(--rule)] bg-[var(--surface)] px-2 py-1.5">
              {statement}
            </code>
          </div>
        )}
      </div>
    </Modal>
  );
}
